<template>
	<v-skeleton-loader v-if="skeleton" type="list-item,list-item,list-item" />
	<v-sheet v-else data-test-selector="adyen_form">

		<div v-if="_loading" class="d-flex justify-center">
			<v-progress-linear indeterminate />
		</div>

		<div data-testid="adyenForm" id="adyen_form_container" v-show="dropinElement"></div>

		<v-checkbox
			data-testid="agreeTnc"
			v-on:click="unlockCardPayments"
			v-model="readTerms"
			:disabled="paying || readTerms"
			name="read_terms"
			:rules="[rules.required]"
			:class="{ 'highlight-error': showTermsError, 'mb-1': true }"
		>
			<template v-slot:label>
				<span>
					<span v-text="$t('paymentForm.creditBalanceDialog.readTerms')"></span>
					&nbsp;<a @click.prevent.stop="handleTermsClick" v-text="$t('paymentForm.creditBalanceDialog.terms')"></a>
				</span>
			</template>
		</v-checkbox>

		<div class="g-recaptcha"
			v-if="enableCaptcha"
			data-callback="onReCaptchaSuccess"
			:data-sitekey="$root.websiteConfig.data.gcp_recaptcha"
			data-action="PAYMENT"
			:class="{ 'highlight-error': showCaptchaError }"
		></div>
	</v-sheet>
</template>

<script>
import AdyenCheckout from "@adyen/adyen-web-old";
import { PaymentService, EventBus, CartModel, BaseModel, Rules, EComService } from '@connectngo/sdk';
import CaptchaMixin from "@/mixins/CaptchaMixin";

export default {
	name: 'AdyenForm',

	mixins: [CaptchaMixin],

	props: {
		cart: {
			type: CartModel,
			default: () => new CartModel(),
		},
		engine: {
			type: BaseModel,
			default: () => new BaseModel(),
		},
		skeleton: {
			type: Boolean,
			default: false,
		},
		balance: {
			type: Number,
			default: null
		}
	},

	data: () => ({
		loading: false,
		paying: false,
		success: false,
		error: false,
		readTerms: false,
		dropinElement: null,
		dropInData: null,
		redirectUrl: null,
		paymentLockSeconds: 0,
		adyenButton: null,
		captchaReady: false,
		showTermsError: false,
		showCaptchaError: false,
	}),

	computed: {
		_loading() {
			return this.loading && !this.dropinElement;
		},
		canPay() {
			const captchaCheck = this.enableCaptcha ? this.captchaReady : true;
			return !this.paying
				&& !this.loading
				&& this.readTerms
				&& captchaCheck;
		},
		klarnaFeatureFlagEnabled() {
			const { feature_flags } = this.$root.websiteConfig.data || {};
			return feature_flags && feature_flags.includes('co-9632-Klarna-payment');
		},
		ancvAydenPaymentIssueFixFeatureFlagEnabled() {
			const { feature_flags } = this.$root?.websiteConfig?.data || {};
			return feature_flags && feature_flags.includes('co-11120-adyen-issue-with-ancv-partial-payment-fix');
		},
	},

	methods: {
		onCaptchaSuccess() {
			this.captchaReady = true;
			this.unlockCardPayments();
		},

		handleTermsClick() {
			EventBus.publish('OPEN_PAGE', 'terms');
		},

		loadDropIn() {
			this.loading = true;

			let amount = this.balance || this.cart.data.total;

			if (this.ancvAydenPaymentIssueFixFeatureFlagEnabled) {
				amount = this.balance || this.cart.data.amountToPay || this.cart.data.total;
			}
			return new PaymentService().getAdyenDropIn(
				this.engine.data.id,
				amount
			)
				.then(response => {
					this.dropInData = response.data;

					let paymentMethodsResponse = response.data.payment_methods;
					// For reservations, exclude Klarna payment methods if feature flag is enabled
					if (this.cart.data.reservation_id && this.klarnaFeatureFlagEnabled) {
						paymentMethodsResponse = this.filterOutKlarna(paymentMethodsResponse);
					}

					const paymentMethodsConfiguration = this.buildPaymentMethodsConfiguration(response.data);

					this.adyenButton = this.createAdyenCheckout(
						paymentMethodsResponse,
						paymentMethodsConfiguration,
						response.data
					);

					return this.mountDropIn(paymentMethodsResponse);
				})
				.catch(error => this.$handleError(this, error))
				.finally(() => this.loading = false);
		},

		filterOutKlarna(paymentMethods) {
			const klarnaTypes = new Set(['klarna', 'klarna_paynow', 'klarna_account', 'klarna_paylater']);
			return {
				...paymentMethods,
				paymentMethods: paymentMethods.paymentMethods.filter(
					pm => !klarnaTypes.has(pm.type)
				)
			};
		},

		buildPaymentMethodsConfiguration(responseData) {
			const paymentMethodsConfiguration = {
				card: {
					hasHolderName: true,
					holderNameRequired: true,
					enableStoreDetails: false,
					billingAddressRequired: false
				}
			};

			// Include Klarna configurations only for non-reservation payments
			if (!this.cart.data.reservation_id) {
				const amount = {
					amount: {
						currency: responseData.currency,
						value: responseData.amount
					}
				};
				paymentMethodsConfiguration.klarna = amount;
				paymentMethodsConfiguration.klarna_paynow = amount;
				paymentMethodsConfiguration.klarna_account = amount;
			}

			return paymentMethodsConfiguration;
		},

		createAdyenCheckout(paymentMethodsResponse, paymentMethodsConfiguration, responseData) {
			return AdyenCheckout({
				paymentMethodsResponse: paymentMethodsResponse,
				paymentMethodsConfiguration: paymentMethodsConfiguration,
				clientKey: responseData.client_key,
				locale: responseData.locale,
				environment: responseData.environment,
				onSubmit: (state, dropin) => {
					this.handleDropInSubmit(state, dropin);
				},
			});
		},

		handleDropInSubmit(state, dropin) {
			// Move checkoutAttemptId from paymentMethod to top level for Adyen API
			if (state.data.paymentMethod.checkoutAttemptId) {
				state.data.checkoutAttemptId = state.data.paymentMethod.checkoutAttemptId;
				delete state.data.paymentMethod.checkoutAttemptId;
			}

			this.showTermsError = false;
			this.showCaptchaError = false;

			if (!this.canPay) {
				if (!this.readTerms) {
					this.$handleError(this, {message:  this.$t('paymentForm.creditBalanceDialog.terms.error')});
					this.showTermsError = true;
				} else {
					this.$handleError(this, {message:  this.$t('paymentForm.creditBalanceDialog.captcha.error')});
					this.showCaptchaError = true;
				}

				dropin.setStatus('ready');
				return;
			}

			// Proceed with payment logic
			this.pay(state, dropin);
		},

		mountDropIn(paymentMethodsResponse) {
			return this.adyenButton.then(checkout => {
				this.dropinElement = checkout.create('dropin', {
					openFirstPaymentMethod: paymentMethodsResponse.paymentMethods.length === 1,
				}).mount('#adyen_form_container');

				return checkout;
			});
		},

		setDropInState(state) {
			this.dropinElement.setStatus(state);
		},

		unlockCardPayments: function () {
			if (this.canPay) {
				this.setDropInState('ready');
			}
		},

		pay(state) {
			// Use type for non-card payment methods (klarna, ideal, etc.) and brand for cards
			const paymentMethod = state.data.paymentMethod.brand || state.data.paymentMethod.type;

			this.paying = true;
			this.error = false;
			this.$emit('paymentOverlay', [this.paying, this.success, this.error]);
			this.dropinElement.setStatus('loading');
			new PaymentService().processAdyenPayment(
				this.engine.data.id,
				this.cart.data.uuid,
				state.data,
				this.redirectUrl,
				this.cart.data.amountToPay,
				this.cart.deposit
			)
				.then(response => {
					this.handlePaymentSuccess(response, paymentMethod)
				})
				.catch(error => {
					this.handlePaymentError(error)
				})
		},
		handlePaymentSuccess(response, paymentMethod) {
			if (response.data.action) {
				this.dropinElement.setStatus('ready');
				this.dropinElement.handleAction(response.data.action);
			} else {
				this.$emit('completed', response);
			}
			this.paying = false;
			this.$emit('paymentOverlay', [this.paying, this.success, this.error]);

			if (paymentMethod) {
				const values = {
					paymentMethod: paymentMethod,
					amount: this.cart.data.amountToPay
				}
				this.$emit('paymentMethodCaptured', values)
			}

		},

		handlePaymentError(error) {
			if (this.$isAlreadyPaid(error)) {
				this.paying = false
				this.$emit('paymentOverlay', [this.paying, this.success, this.error]);
				this.$emit('completed');
			}

			if (this.$isAlreadyLocked(error) && this.paymentLockSeconds < error.timeout) {
				this.checkPaymentLock();
			} else {
				this.loadCaptcha();
				this.paymentLockSeconds = 0;
				this.paying = false;
				this.error = true;
				this.dropinElement.setStatus('error', error);
				this.$emit('paymentOverlay', [this.paying, this.success, this.error]);
				setTimeout(() => {
					this.dropinElement.setStatus('ready');
				}, 3000);
			}
		},

		checkPaymentLock() {
			return new EComService().getPaymentLockStatus(this.cart.data.uuid)
				.then(response => {
					this.paymentLockSeconds = 0;
					this.handlePaymentSuccess(response);
				})
				.catch(error => {
					this.paymentLockSeconds += 2;
					setTimeout(() => {
						this.paying = true;
						this.$emit('paymentOverlay', [this.paying, this.success, this.error]);
						this.dropinElement.setStatus('loading');
						this.handlePaymentError(error);
					}, 2000)
				})
		},

		doCallback(paymentId, redirectResult) {
			new PaymentService().doAdyenCallback(paymentId, redirectResult)
				.then(response => {
					this.error = false;
					this.$emit('paymentOverlay', [this.paying, this.success, this.error]);
					this.$emit('completed', response);
				})
				.catch(error => {
					this.error = true;
					this.$emit('paymentOverlay', [this.paying, this.success, this.error]);
					this.dropinElement.setStatus('error', error);
					setTimeout(() => {
						this.dropinElement.setStatus('ready');
					}, 3000);
				});
		}
	},

	created() {
		this.rules = {
			required: value => Rules.required(value) || this.$t('rules.required'),
			creditCardNumber: value => Rules.creditCardNumber(value) || this.$t('rules.creditCardNumber'),
			creditCardCvc: value => Rules.creditCardCvc(value) || this.$t('rules.creditCardCvc'),
			creditCardExp: value => Rules.creditCardExp(value) || this.$t('rules.creditCardExp'),
			zipPostal: value => Rules.zipCode(value) || Rules.postalCode(value) || this.$t('rules.creditCardZipPostal'),
		};

		const threeDsResolutionRoutes =  ['cart_payment_3ds', 'reservation_3ds'];
		if (threeDsResolutionRoutes.includes(this.$route.name)) {
			this.doCallback(this.$route.params.id, this.$route.query.redirectResult);
		}

		this.loadDropIn();

		const reservationRoutes = ['reservation', 'reservation_3ds'];
		if (reservationRoutes.includes(this.$route.name)) {
			this.redirectUrl = window.location.origin + this.$router.resolve(
				{
					name: 'reservation_3ds_prepare',
					params: this.$route.params
				}
			).href;
		} else {
			this.redirectUrl = window.location.origin + this.$router.resolve({ name: 'cart_payment_3ds_prepare', params: { step: 'payment', engine: 'adyen_ecom' } }).href;
		}

		if (this.$route.query.redirectResult) {
			this.readTerms = true;
		}
	},

	mounted() {
		this.$on('captcha-success', this.onCaptchaSuccess);
	},
}
</script>

<style scoped>
.highlight-error {
	box-shadow: 0 0 0 2px #ff5252;
	border-radius: 4px;
	transition: box-shadow 0.3s;
}
</style>
